import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Images } from '@/assets';
import { colors } from '@/theme/colors';

interface InputProps {
  id: string;
  label: string;
  type?: 'text' | 'password' | 'email' | 'number';
  value: string;
  errorMessage?: string;
  placeholder?: string;
  height?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const Input = ({
  id,
  label,
  type = 'text',
  value,
  errorMessage = '',
  placeholder,
  height = '52px',
  onChange,
  onBlur,
}: InputProps) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const onClickEyeIcon = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const inputType = useMemo(() => {
    if (type === 'password') {
      return showPassword ? 'text' : 'password';
    }
    return type;
  }, [type, showPassword]);

  return (
    <Wrapper>
      <Section>
        <Label htmlFor={id}>{label}</Label>
        <InputWrapper>
          <StyledInput
            id={id}
            type={inputType}
            value={value}
            data-error={!!errorMessage}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            height={height}
          />
          {type === 'password' && (
            <EyeIcon onClick={onClickEyeIcon} data-visible={showPassword}>
              <img
                src={showPassword ? Images.IconEyeOpen : Images.IconEyeClose}
                alt="toggle-password-visibility"
                width={24}
                height={24}
              />
            </EyeIcon>
          )}
        </InputWrapper>
      </Section>
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </Wrapper>
  );
};

export default Input;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 12px;
  text-align: left;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 700;
`;

interface StyledInputProps {
  height: string;
}

const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input<StyledInputProps>`
  border: 1px solid ${colors.inputBorder};
  padding: 12.5px 24px;
  width: calc(100% - 48px);
  height: ${({ height }) => `calc(${height} - 25px)`};
  border-radius: 26px;
  font-size: 14px;

  &::placeholder {
    color: ${colors.placeholder};
  }
  &:focus {
    outline: none;
  }

  &[data-error='true'] {
    border: 1px solid ${colors.error};
  }
`;

const EyeIcon = styled.div`
  width: 24px;
  height: 24px;
  position: absolute;
  top: 12.5px;
  right: 24px;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  color: ${colors.error};
  height: 16px;
  font-size: 12.8px;
`;
